import { Navigate, useLocation } from "react-router-dom";

import {
  AuthRole,
  type AuthStage,
  type RestaurantAccess,
  RestaurantRole,
  StageAuth,
  type UserRole,
} from "@/store/AuthStore";
import { rootStore } from "@/store/RootStore";
import { buildLoginPathWithReturnUrl } from "@/utils/returnUrl";

import { getAppLoginPath, PATHS } from "./path";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredStage?: AuthStage;
  requiredRole?: UserRole | UserRole[];
  requiredAccess?: RestaurantAccess;
}

export const ProtectedRoute = ({
  children,
  requiredStage,
  requiredRole,
  requiredAccess,
}: ProtectedRouteProps) => {
  const { pathname, search } = useLocation();
  const { stage, role, restaurantAccess, token, isAdmin } = rootStore.authStore;
  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  if (!token) {
    const returnUrl = `${pathname}${search}`;
    return (
      <Navigate
        to={buildLoginPathWithReturnUrl(getAppLoginPath(pathname), returnUrl)}
        replace
      />
    );
  }

  if (requiredStage && stage !== requiredStage) {
    return <Navigate to={getRedirectPath(stage, pathname)} replace />;
  }

  if (!roles.includes(role)) {
    return <Navigate to={getRedirectPathByRole(role, pathname)} replace />;
  }

  if (isAdmin && (requiredRole === AuthRole.RESTAURANT || requiredAccess)) {
    return <Navigate to={PATHS.ADMIN.ROOT} replace />;
  }

  if (
    requiredRole === AuthRole.RESTAURANT &&
    requiredAccess &&
    restaurantAccess !== requiredAccess
  ) {
    return <Navigate to={getRedirectPathByAccess(restaurantAccess)} replace />;
  }

  return <>{children}</>;
};

function getRedirectPath(requiredStage: StageAuth, pathname: string) {
  switch (requiredStage) {
    case StageAuth.AUTHENTICATED:
      return getAppLoginPath(pathname);
    case StageAuth.ONBOARDING:
      return PATHS.ONBOARDING.ROOT;
    case StageAuth.UNAUTHENTICATED:
      return PATHS.HOME;
    default:
      return getAppLoginPath(pathname);
  }
}

function getRedirectPathByRole(role: string | null, pathname: string) {
  if (rootStore.authStore.isAdmin) {
    return PATHS.ADMIN.ROOT;
  }

  if (role === AuthRole.RESTAURANT) {
    const access = rootStore.authStore.restaurantAccess;

    switch (access) {
      case RestaurantRole.GROUP:
        return PATHS.RESTAURANT.GROUP.ROOT;
      case RestaurantRole.BRAND:
        return PATHS.RESTAURANT.BRAND.ROOT;
      case RestaurantRole.BRANCH:
        return PATHS.RESTAURANT.BRANCH.ROOT;
      default:
        return PATHS.RESTAURANT.ROOT;
    }
  }

  return getAppLoginPath(pathname);
}

export function getRedirectPathByAccess(access: string | null) {
  switch (access) {
    case RestaurantRole.GROUP:
      return PATHS.RESTAURANT.GROUP.ROOT;
    case RestaurantRole.BRAND:
      return PATHS.RESTAURANT.BRAND.ROOT;
    case RestaurantRole.BRANCH:
      return PATHS.RESTAURANT.BRANCH.ROOT;
    default:
      return PATHS.RESTAURANT.ROOT;
  }
}
